import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Btn from 'components/buttons/Btn'
import { FixedWarning } from 'components/FixedWarnings/FixedWarning'
import I from 'components/I'
import TypeAndPmtRow from 'pages/Order/Header/TypeAndPmtRow'
import PropTypes from 'prop-types'
import { useContext } from 'react'
import { Alert, Badge, ButtonGroup, Col, Row } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getCannedMessages } from 'state/Account'
import { useServerConstants } from 'state/Constants'
import Order from 'state/models/Order'
import { toggleVIP } from 'state/Orders'
import { FMT, klass, notifyOrder, platform, useBoolState, useModalState } from 'utils'
import OrderContext from '../_context'
import NotifyModal from '../Modals/NotifyModal'
import AddressRow from './AddressRow'
import DatesRow from './DatesRow'
import Ellipsis from './Ellipsis'
import GroupOrderRow from './GroupOrderRow'

//FIXME: when this switches to the final content, there's a slight movement - rows probably don't have the same height

/**
 * @param {boolean} isCancelled
 * @param {boolean} isCompleted
 * @param {boolean} temp If this is a temporary, partially loaded order, or a complete one
 */
export default function Header({ isCancelled, isCompleted, temp = false }) {
	const { t }     = useTranslation()
	const dispatch  = useDispatch()
	const CONSTANTS = useServerConstants()

	/** @type Order */
	const order   = useContext(OrderContext)
	const dev     = useBoolState(!platform.isProd())
	const vip     = useBoolState(order.is_customer_vip || false)

	const notifyModal = useModalState()

	/** @type ?Restaurant */
	const restaurant = useSelector(order.getRestaurantIfNeedsDistinction)

	const hasMsgs = useSelector(getCannedMessages).hasOwnProperty(1) //probably can be dropped in a few weeks, since everyone will have re-logged in after a couple of weeks/months?

	function devNotify() {
		if (dev.isnt) {
			return false
		}
		window.setTimeout(() => notifyOrder(order, { force: true }), 5000)
	}

	function doToggleVIP() {
		vip.toggle(vip.isnt)
		dispatch(toggleVIP(order.customer_id, vip.isnt)) //here, `vip` haven't flipped yet...
			.catch(e => console.error(e) || vip.toggle(vip.is)) //...but at the time the catch is triggered, it has flipped
	}

	return <div id="basic-details" {...klass({ hasSpecialStatus: isCancelled || isCompleted })}>
		{(isCompleted || isCancelled) &&
			<div id="special-status" {...klass({ textDanger: isCancelled, textSuccess: isCompleted })}>
				<p>{isCancelled? t('Canceled') : (isCompleted? t('Completed') : '')}</p>
			</div>}

{/* ************************************************ Top-page alerts *********************************************** */}
		<div className="top-inner-alerts">
			<FixedWarning variant="danger" {...Order.ICONS[Order.STATUS.FUTURE]} show={order.is_considered_future}>
				<Trans>This order is scheduled for <b>{{ date: FMT.date(order.ready_at, order.not_for_today? { noTime: true, day: 'do' } : { onlyTime: true })}}</b></Trans>
			</FixedWarning>

			{restaurant && <Alert className="alert-restaurant" style={{ backgroundColor: restaurant.color }}>{restaurant.display_name}</Alert>}

			{order.is_on_springroll? <Alert className="alert-restaurant springroll-bg">{t('via Springroll.com')}</Alert> : null}
		</div>

{/* **************************************************** Customer ************************************************** */}
		<Row>
			<Col>
				<Trans><label>For</label> {{ customer: order.customer_name }}</Trans>
				<Btn className={vip.is? 'VIP active' : 'VIP'} iconSize="lg" size="sm" variant="outline"
				     swap icon={[vip.is? 'fas' : 'far', 'star']} onClick={doToggleVIP}
				     title={vip.is?
					     t('VIP customer; {{tap}} to remove', { tap: platform.clickOrTap(t) }) :
					     t('Normal customer; {{tap}} to mark as VIP', { tap: platform.clickOrTap(t) })}
				/>
			</Col>
			<Col xs="auto">
				{temp? <Ellipsis/> :
					<ButtonGroup vertical={platform.isXSmall()} className="my-n1">
						<Btn swap fixedWidth size="sm" variant="light" icon="phone-alt" href={`tel:${order.customer_current_phone}`}>
							{order.customer_current_phone}
						</Btn>
						{/* TODO start using time constants if this becomes common */}
						{hasMsgs && order.diff_hours > -24 && <>
							<Btn swap fixedWidth size="sm" variant="secondary" icon="envelope" onClick={notifyModal.doOpen}>
								<span className="d-block d-sm-none">{t('Send message')}</span>
							</Btn>
						</>}
					</ButtonGroup>}
			</Col>
		</Row>
		{hasMsgs && <NotifyModal {...notifyModal} isCancelled={isCancelled}/>}

{/* ***************************************************** Dates **************************************************** */}
		<DatesRow {...{ temp, isCompleted, isCancelled }} />

{/* ****************************** Order type, third-party delivery and payment method ***************************** */}
		<TypeAndPmtRow {...{ temp }}/>

{/* ********************************************* Address + Group order ******************************************** */}
		{!temp && <AddressRow/>}
		{!temp && <GroupOrderRow/>}

{/* ************************************************* Custom fields ************************************************ */}
		{!temp && order.custom_fields.length > 0 && <Row id="custom-fields">
			{order.custom_fields.map(([name, value]) => (
				<Col xs="auto" key={name}>
					{name == CONSTANTS.VALUES.CUSTOM_FIELDS.CUTLERY?
						<><Badge variant="danger">{t('No', { context: 'without' })}</Badge> <label>{t('utensils')}</label></> :
						name == CONSTANTS.VALUES.CUSTOM_FIELDS.NAPKINS?
							<><Badge variant="danger">{t('No', { context: 'without' })}</Badge> <label>{t('napkins')}</label></> :
							<><small className="text-muted">{name}</small>{' '}<strong>{value}</strong></>
					}
				</Col>
			))}
		</Row>}

{/* *********************************************** Dev notify button ********************************************** */}
{/* eslint-disable i18next/no-literal-string */}
		{dev.is && <Row className="d-print-none">
			<Col className="pr-0">
				<abbr title="This entire row only shows on development environments, to aid with testing order notification - you can hide the row (for screenshots) with the 'Remove' button. 'Notify' behaves just like a notification coming through Push/Donkey - it will flash/popup/sound/print. The only caveat is: it skips time verification, so if the order is early, it will still notify (AKA skips `newOrderWhen`)." style={{cursor:'help'}}><small><I force>Dev&nbsp;tools:</I></small></abbr>
				<Btn block size="sm" variant="outline-dark" swap icon="bell" onClick={devNotify} className="mx-2">
					Notify in-app after 5s
				</Btn>
			</Col>
			<Col xs="auto">
				<Btn size="sm" variant="outline-danger" swap icon="times" onClick={dev.off}>Hide</Btn>
			</Col>
		</Row>}
{/* eslint-enable i18next/no-literal-string */}
{/* ********************************************** Flag & Order notes ********************************************* */}
		{order.is_flagged && <Alert variant="warning" className="text-warning font-weight-bold">
			<FontAwesomeIcon fixedWidth icon="pennant"/> {t('This customer is flagged — call before preparing the order.')}
		</Alert>}

		{order.special_instructions && <Alert variant="note">
			<FontAwesomeIcon fixedWidth icon="comment-dots"/> {order.special_instructions}
		</Alert>}
		{order.driver_notes && <Alert variant="note">
			<FontAwesomeIcon fixedWidth icon="car-on"/> {order.driver_notes}
		</Alert>}
	</div>
}

Header.propTypes = {
	temp       : PropTypes.bool,
	isCancelled: PropTypes.oneOf([true, false, null]).isRequired,
	isCompleted: PropTypes.oneOf([true, false, null]).isRequired,
}
